import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CalendarDays, Plus, Trash2, Edit2, Loader2, Users, MapPin, Clock,
  ChevronLeft, ChevronRight, UserCheck, AlertCircle, Search, Smartphone, Key, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const AdminEinsatzplanung = () => {
  const [members, setMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingAssign, setEditingAssign] = useState(null);

  const [memberForm, setMemberForm] = useState({ name: '', phone: '', email: '', role: '', skills: '', status: 'verfügbar', hourly_wage: '', enableClock: false, clockEmail: '', clockPassword: '' });
  const [assignForm, setAssignForm] = useState({ member_id: '', member_name: '', date: selectedDate, project: '', address: '', start_time: '07:00', end_time: '16:00', notes: '' });
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginTarget, setLoginTarget] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersRes, assignRes] = await Promise.all([
        api.get('/admin/team-members'),
        api.get('/admin/assignments', { params: { date: selectedDate } })
      ]);
      setMembers(membersRes.data || []);
      setAssignments(assignRes.data || []);
    } catch (e) { toast.error('Fehler beim Laden'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [selectedDate]);

  const searchCustomers = async (q) => {
    if (q.length < 1) { setCustomers([]); setShowCustomerDropdown(false); return; }
    try {
      const res = await api.get('/customers', { params: { search: q } });
      setCustomers(Array.isArray(res.data) ? res.data : []);
      setShowCustomerDropdown(true);
    } catch (e) { /* ignore */ }
  };

  const selectCustomerForAssign = (c) => {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company || '';
    const addr = `${c.street || ''}, ${c.postal_code || ''} ${c.city || ''}`.trim().replace(/^,\s*/, '');
    setAssignForm(p => ({ ...p, project: name, address: addr }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Member CRUD
  const openNewMember = () => { setEditingMember(null); setMemberForm({ name: '', phone: '', email: '', role: '', skills: '', status: 'verfügbar', hourly_wage: '', enableClock: false, clockEmail: '', clockPassword: '' }); setShowPassword(false); setShowMemberDialog(true); };
  const openEditMember = (m) => { setEditingMember(m); setMemberForm({ name: m.name || '', phone: m.phone || '', email: m.email || '', role: m.role || '', skills: (m.skills || []).join(', '), status: m.status || 'verfügbar', hourly_wage: m.hourly_wage != null ? String(m.hourly_wage) : '', enableClock: false, clockEmail: '', clockPassword: '' }); setShowMemberDialog(true); };

  const saveMember = async () => {
    if (!memberForm.name) { toast.error('Name ist Pflicht'); return; }
    if (!editingMember && memberForm.enableClock) {
      if (!memberForm.clockEmail || !memberForm.clockPassword) { toast.error('Stempeluhr-Zugang: E-Mail und Passwort sind Pflicht'); return; }
      if (memberForm.clockPassword.length < 6) { toast.error('Passwort muss mindestens 6 Zeichen lang sein'); return; }
    }
    const data = {
      name: memberForm.name,
      phone: memberForm.phone,
      email: memberForm.email,
      role: memberForm.role,
      status: memberForm.status,
      skills: memberForm.skills.split(',').map(s => s.trim()).filter(Boolean),
      hourly_wage: memberForm.hourly_wage === '' ? null : Number(memberForm.hourly_wage),
    };
    try {
      if (editingMember) {
        await api.put(`/admin/team-members/${editingMember.id}`, data);
        toast.success('Aktualisiert');
      } else {
        const res = await api.post('/admin/team-members', data);
        const newId = res.data?.id;
        if (memberForm.enableClock && newId) {
          try {
            await api.post(`/admin/team-members/${newId}/create-login`, {
              email: memberForm.clockEmail.trim().toLowerCase(),
              password: memberForm.clockPassword,
            });
            toast.success('Mitarbeiter + Stempeluhr-Zugang erstellt');
          } catch (le) {
            toast.error('Mitarbeiter erstellt, aber Zugang fehlgeschlagen: ' + (le.response?.data?.detail || 'Fehler'));
          }
        } else {
          toast.success('Mitarbeiter erstellt');
        }
      }
      setShowMemberDialog(false); loadData();
    } catch (e) { toast.error('Fehler'); }
  };

  const deleteMember = async (id) => {
    if (!confirm('Mitarbeiter löschen?')) return;
    try { await api.delete(`/admin/team-members/${id}`); toast.success('Gelöscht'); loadData(); } catch (e) { toast.error('Fehler'); }
  };

  const openLoginDialog = (member) => {
    setLoginTarget(member);
    setLoginForm({ email: member.email || '', password: '' });
    setShowPassword(false);
    setShowLoginDialog(true);
  };

  const createLogin = async () => {
    if (!loginForm.email || !loginForm.password) { toast.error('E-Mail und Passwort sind Pflicht'); return; }
    if (loginForm.password.length < 6) { toast.error('Passwort muss mindestens 6 Zeichen lang sein'); return; }
    setLoginLoading(true);
    try {
      await api.post(`/admin/team-members/${loginTarget.id}/create-login`, loginForm);
      toast.success(`Stempeluhr-Zugang erstellt für ${loginTarget.name}`);
      setShowLoginDialog(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    }
    setLoginLoading(false);
  };

  const deleteLogin = async (memberId, memberName) => {
    if (!confirm(`Stempeluhr-Zugang für ${memberName} löschen?`)) return;
    try {
      await api.delete(`/admin/team-members/${memberId}/delete-login`);
      toast.success('Zugang gelöscht');
      loadData();
    } catch (e) { toast.error('Fehler'); }
  };

  // Assignment CRUD
  const openNewAssign = (memberId, memberName) => {
    setEditingAssign(null);
    setAssignForm({ member_id: memberId || '', member_name: memberName || '', date: selectedDate, project: '', address: '', start_time: '07:00', end_time: '16:00', notes: '' });
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setShowAssignDialog(true);
  };

  const openEditAssign = (a) => {
    setEditingAssign(a);
    setAssignForm({ member_id: a.member_id || '', member_name: a.member_name || '', date: a.date || selectedDate, project: a.project || '', address: a.address || '', start_time: a.start_time || '07:00', end_time: a.end_time || '16:00', notes: a.notes || '' });
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setShowAssignDialog(true);
  };

  const saveAssign = async () => {
    if (!assignForm.member_id || !assignForm.project) { toast.error('Mitarbeiter und Projekt sind Pflicht'); return; }
    try {
      if (editingAssign) { await api.put(`/admin/assignments/${editingAssign.id}`, assignForm); toast.success('Aktualisiert'); }
      else { await api.post('/admin/assignments', assignForm); toast.success('Einsatz erstellt'); }
      setShowAssignDialog(false); loadData();
    } catch (e) { toast.error('Fehler'); }
  };

  const deleteAssign = async (id) => {
    if (!confirm('Einsatz löschen?')) return;
    try { await api.delete(`/admin/assignments/${id}`); toast.success('Gelöscht'); loadData(); } catch (e) { toast.error('Fehler'); }
  };

  const STATUS_COLORS = { 'verfügbar': 'bg-green-100 text-green-700', 'krank': 'bg-red-100 text-red-700', 'urlaub': 'bg-blue-100 text-blue-700', 'unterwegs': 'bg-yellow-100 text-yellow-700' };

  const getAssignmentsForMember = (memberId) => assignments.filter(a => a.member_id === memberId);
  const unassignedMembers = members.filter(m => !assignments.some(a => a.member_id === m.id) && m.status === 'verfügbar');

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#2563eb] to-[#3b82f6] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="einsatzplanung-title">
                <CalendarDays className="w-8 h-8" /> Einsatzplanung
              </h1>
              <p className="text-white/80 mt-1">Mitarbeiter den Baustellen zuweisen</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" onClick={openNewMember} data-testid="new-member-btn">
                <Users className="w-4 h-4 mr-2" /> Mitarbeiter
              </Button>
              <Button className="bg-white text-[#2563eb] hover:bg-gray-100" onClick={() => openNewAssign('', '')} data-testid="new-assign-btn">
                <Plus className="w-4 h-4 mr-2" /> Einsatz
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center justify-between bg-white rounded-lg border p-3">
          <Button variant="ghost" size="sm" onClick={() => changeDate(-1)}><ChevronLeft className="w-5 h-5" /></Button>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900" data-testid="current-date">{formatDate(selectedDate)}</p>
            <Button variant="link" size="sm" className="text-blue-600" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}>Heute</Button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => changeDate(1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>
      </div>

      {/* Team Status Cards */}
      <div className="max-w-6xl mx-auto mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-600">{members.length}</p><p className="text-xs text-gray-500">Mitarbeiter</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{assignments.length}</p><p className="text-xs text-gray-500">Einsätze heute</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-yellow-600">{unassignedMembers.length}</p><p className="text-xs text-gray-500">Verfügbar</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-600">{members.filter(m => m.status === 'krank' || m.status === 'urlaub').length}</p><p className="text-xs text-gray-500">Abwesend</p></CardContent></Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Assigned Members */}
          {members.map(member => {
            const memberAssignments = getAssignmentsForMember(member.id);
            return (
              <Card key={member.id} data-testid={`member-card-${member.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                        {(member.name || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{member.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          {member.role && <span>{member.role}</span>}
                          {member.phone && <span>{member.phone}</span>}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[member.status] || 'bg-gray-100 text-gray-600'}`}>{member.status}</span>
                      {member.login_email && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1" data-testid={`login-badge-${member.id}`}>
                          <Smartphone className="w-3 h-3" /> Stempeluhr
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 mt-2 sm:mt-0">
                      <Button variant="ghost" size="sm" onClick={() => openNewAssign(member.id, member.name)} title="Einsatz zuweisen"><Plus className="w-4 h-4" /></Button>
                      {member.login_email ? (
                        <Button variant="ghost" size="sm" onClick={() => deleteLogin(member.id, member.name)} title={`Zugang: ${member.login_email}`} className="text-green-600" data-testid={`manage-login-${member.id}`}>
                          <Smartphone className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => openLoginDialog(member)} title="Stempeluhr-Zugang erstellen" className="text-gray-400 hover:text-blue-600" data-testid={`create-login-${member.id}`}>
                          <Key className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEditMember(member)}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteMember(member.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {memberAssignments.length > 0 ? (
                    <div className="space-y-2 ml-13">
                      {memberAssignments.map(a => (
                        <div key={a.id} className="flex items-center justify-between bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div>
                            <p className="font-medium text-gray-900">{a.project}</p>
                            <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                              {a.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.address}</span>}
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{a.start_time} - {a.end_time}</span>
                            </div>
                            {a.notes && <p className="text-xs text-gray-400 mt-1">{a.notes}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditAssign(a)}><Edit2 className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteAssign(a.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : member.status === 'verfügbar' ? (
                    <p className="text-sm text-gray-400 ml-13 italic">Kein Einsatz zugewiesen</p>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
          {members.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Noch keine Mitarbeiter angelegt</p>
              <Button variant="outline" className="mt-3" onClick={openNewMember}><Plus className="w-4 h-4 mr-2" /> Ersten Mitarbeiter anlegen</Button>
            </div>
          )}
        </div>
      )}

      {/* Member Dialog */}
      <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingMember ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <div><Label>Name *</Label><Input value={memberForm.name} onChange={(e) => setMemberForm(p => ({...p, name: e.target.value}))} className="mt-1" data-testid="input-member-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon</Label><Input value={memberForm.phone} onChange={(e) => setMemberForm(p => ({...p, phone: e.target.value}))} className="mt-1" /></div>
              <div><Label>E-Mail</Label><Input value={memberForm.email} onChange={(e) => setMemberForm(p => ({...p, email: e.target.value}))} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Rolle</Label><Input value={memberForm.role} onChange={(e) => setMemberForm(p => ({...p, role: e.target.value}))} placeholder="z.B. Maler, Vorarbeiter" className="mt-1" /></div>
              <div><Label>Status</Label>
                <Select value={memberForm.status} onValueChange={(v) => setMemberForm(p => ({...p, status: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verfügbar">Verfügbar</SelectItem>
                    <SelectItem value="krank">Krank</SelectItem>
                    <SelectItem value="urlaub">Urlaub</SelectItem>
                    <SelectItem value="unterwegs">Unterwegs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fähigkeiten (kommagetrennt)</Label><Input value={memberForm.skills} onChange={(e) => setMemberForm(p => ({...p, skills: e.target.value}))} placeholder="z.B. Fassade, Innen, Lackieren" className="mt-1" /></div>
              <div>
                <Label>Stundenlohn (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={memberForm.hourly_wage}
                  onChange={(e) => setMemberForm(p => ({...p, hourly_wage: e.target.value}))}
                  placeholder="z.B. 18.50"
                  className="mt-1"
                  data-testid="input-member-hourly-wage"
                />
              </div>
            </div>

            {!editingMember && (
              <div className="border-t pt-3 mt-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={memberForm.enableClock}
                    onChange={(e) => setMemberForm(p => ({ ...p, enableClock: e.target.checked, clockEmail: e.target.checked ? (p.email || '') : '', clockPassword: '' }))}
                    className="w-4 h-4"
                    data-testid="checkbox-enable-clock"
                  />
                  <Smartphone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium">Stempeluhr-Zugang direkt erstellen</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Mitarbeiter kann sich damit selbst ein- und ausstempeln.</p>

                {memberForm.enableClock && (
                  <div className="mt-3 space-y-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div>
                      <Label>Login E-Mail *</Label>
                      <Input
                        type="email"
                        value={memberForm.clockEmail}
                        onChange={(e) => setMemberForm(p => ({ ...p, clockEmail: e.target.value }))}
                        placeholder="z.B. hasan@farbwelt-service.de"
                        className="mt-1"
                        data-testid="input-clock-email"
                      />
                    </div>
                    <div>
                      <Label>Passwort *</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={memberForm.clockPassword}
                          onChange={(e) => setMemberForm(p => ({ ...p, clockPassword: e.target.value }))}
                          placeholder="Mindestens 6 Zeichen"
                          className="pr-10"
                          data-testid="input-clock-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMemberDialog(false)}>Abbrechen</Button>
            <Button onClick={saveMember} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="save-member-btn">{editingMember ? 'Speichern' : 'Anlegen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingAssign ? 'Einsatz bearbeiten' : 'Neuer Einsatz'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-4">
            <div><Label>Mitarbeiter *</Label>
              <Select value={assignForm.member_id || undefined} onValueChange={(v) => { const m = members.find(x => x.id === v); setAssignForm(p => ({...p, member_id: v, member_name: m?.name || ''})); }}>
                <SelectTrigger className="mt-1" data-testid="select-assign-member"><SelectValue placeholder="Mitarbeiter wählen..." /></SelectTrigger>
                <SelectContent>{members.map(m => <SelectItem key={m.id} value={m.id}>{m.name} {m.status !== 'verfügbar' ? `(${m.status})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Label>Projekt / Baustelle *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={assignForm.project}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAssignForm(p => ({ ...p, project: val }));
                    searchCustomers(val);
                  }}
                  onFocus={() => { if (assignForm.project.length >= 1) setShowCustomerDropdown(true); }}
                  placeholder="Kundenname suchen oder eingeben..."
                  className="pl-10"
                  data-testid="input-assign-project"
                />
              </div>
              {showCustomerDropdown && customers.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {customers.map(c => (
                    <div key={c.id} className="p-2 hover:bg-blue-50 cursor-pointer text-sm" onClick={() => selectCustomerForAssign(c)}>
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      {c.company && <span className="text-gray-500 ml-1">({c.company})</span>}
                      {c.city && <span className="text-gray-400 ml-2">{c.city}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div><Label>Adresse</Label><Input value={assignForm.address} onChange={(e) => setAssignForm(p => ({...p, address: e.target.value}))} placeholder="z.B. Kölner Str. 42" className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Datum</Label><Input type="date" value={assignForm.date} onChange={(e) => setAssignForm(p => ({...p, date: e.target.value}))} className="mt-1" /></div>
              <div><Label>Von</Label><Input type="time" value={assignForm.start_time} onChange={(e) => setAssignForm(p => ({...p, start_time: e.target.value}))} className="mt-1" /></div>
              <div><Label>Bis</Label><Input type="time" value={assignForm.end_time} onChange={(e) => setAssignForm(p => ({...p, end_time: e.target.value}))} className="mt-1" /></div>
            </div>
            <div><Label>Notizen</Label><Input value={assignForm.notes} onChange={(e) => setAssignForm(p => ({...p, notes: e.target.value}))} placeholder="z.B. Schlüssel beim Nachbarn" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Abbrechen</Button>
            <Button onClick={saveAssign} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="save-assign-btn">{editingAssign ? 'Speichern' : 'Zuweisen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stempeluhr-Zugang Dialog */}
      <Dialog open={showLoginDialog} onOpenChange={setShowLoginDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-blue-600" /> Stempeluhr-Zugang erstellen
            </DialogTitle>
          </DialogHeader>
          {loginTarget && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-gray-900">{loginTarget.name}</p>
                <p className="text-sm text-gray-500">Mitarbeiter bekommt einen eigenen Login für die Stempeluhr-App</p>
              </div>
              <div>
                <Label>E-Mail-Adresse *</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="z.B. max@farbwelt-service.de"
                  className="mt-1"
                  data-testid="input-login-email"
                />
              </div>
              <div>
                <Label>Passwort *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Mindestens 6 Zeichen"
                    className="pr-10"
                    data-testid="input-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-600">
                <p className="font-medium mb-1">So funktioniert es:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-xs">
                  <li>Mitarbeiter öffnet die App auf dem Handy</li>
                  <li>Loggt sich mit E-Mail und Passwort ein</li>
                  <li>Wird automatisch zur Stempeluhr weitergeleitet</li>
                </ol>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoginDialog(false)}>Abbrechen</Button>
            <Button
              onClick={createLogin}
              disabled={loginLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-create-login-btn"
            >
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Zugang erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEinsatzplanung;
